import React, { createContext, forwardRef, useContext } from 'react'
import type { CSSObject } from '@emotion/react'
import { styled, type SxProps, type Theme } from '@mui/material/styles'

import {
  attachPlatformMethods,
  roleBaseStyle,
  toDOMProps,
  useLayoutHandler,
  useMergedRef,
  type LayoutEvent,
  type RNDOMProps
} from './dom'
import {
  resolveStyleProps,
  type RNStyle,
  type StyleGroup,
  type StyleLayers
} from './style'

/* -----------------------------------------------------------------------------
 * KitElement
 *
 * The DOM element every kit component renders: the kit's resolved CSS, plus
 * MUI's `sx` for code that wants to style it the MUI way.
 * -------------------------------------------------------------------------- */

// `as` must not be forwarded: emotion only swaps the element when it is not
const NOT_FORWARDED = new Set(['kitCss', 'sx', 'ownerState', 'theme', 'as'])

// typed loosely on purpose: every kit component builds its props itself
export const KitElement = styled('div', {
  shouldForwardProp: prop => !NOT_FORWARDED.has(prop as string)
})(
  ({ kitCss }: { kitCss: CSSObject }) => kitCss as any
) as unknown as React.ComponentType<any>

// a View inside a Text renders inline, as in react-native-web
export const TextAncestorContext = createContext(false)

// react-native-web's View reset
export const VIEW_BASE: RNStyle = {
  alignItems: 'stretch',
  borderWidth: 0,
  borderStyle: 'solid',
  borderColor: 'black',
  boxSizing: 'border-box',
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexShrink: 0,
  margin: 0,
  minHeight: 0,
  minWidth: 0,
  padding: 0,
  position: 'relative',
  zIndex: 0
}

export type KitViewProps = RNDOMProps & {
  children?: React.ReactNode
  style?: any
  sx?: SxProps<Theme>
  onLayout?: (event: LayoutEvent) => void
  // registered/inline styles a kit component adds around the caller's props
  kitLayers?: Omit<StyleLayers, 'base'>
  [prop: string]: any
}

type ViewOptions = {
  displayName: string
  groups: StyleGroup
  base?: RNStyle
  tag?: string
}

/**
 * A react-native-web View: `groups` are the style props it takes, `base` its
 * reset. Everything else behaves like the View the @walrus/common kit wrapped.
 */
export const createView = ({
  displayName,
  groups,
  base = VIEW_BASE,
  tag = 'div'
}: ViewOptions) => {
  const View = forwardRef<HTMLElement, KitViewProps>(
    ({ onLayout, sx, kitLayers, ...props }, ref) => {
      const inText = useContext(TextAncestorContext)
      const layoutRef = useLayoutHandler(onLayout)
      const mergedRef = useMergedRef(ref, layoutRef, attachPlatformMethods)

      const { css, rest } = resolveStyleProps(props, groups, {
        base: {
          ...base,
          ...(inText ? { display: 'inline-flex' } : {}),
          ...roleBaseStyle(props, tag)
        },
        ...kitLayers
      })
      const { domProps, element, pointerStyle, pointerChildren } =
        toDOMProps(rest)

      return (
        <KitElement
          as={(element || tag) as any}
          ref={mergedRef}
          kitCss={{ ...css, ...pointerStyle, ...pointerChildren } as CSSObject}
          sx={sx}
          {...domProps}
        />
      )
    }
  )
  View.displayName = displayName
  return View
}
